import React, { useState, useEffect, useCallback } from 'react';
import { useStore } from '../../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { EnvelopeSimple, PaperPlaneTilt, CircleNotch, ShieldCheck, Warning, ClockCounterClockwise, Eye, EyeSlash, ArrowsClockwise } from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMAIL_TYPE_LABELS = {
  order_confirmation: 'Confirmation commande',
  admin_alert: 'Alerte admin',
  status_update: 'Suivi commande',
  review_request: 'Demande d\'avis',
  test: 'Test',
};

const SmtpSettingsPage = () => {
  const { currentStore } = useStore();
  const [settings, setSettings] = useState({
    enabled: false, host: '', port: 587, username: '',
    password: '', from_email: '', from_name: '', use_tls: true,
    encryption: 'starttls',
  });
  const [hasSavedPassword, setHasSavedPassword] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [viewedEmail, setViewedEmail] = useState(null);
  const [resendingId, setResendingId] = useState(null);

  const handleResend = async (log) => {
    setResendingId(log.id);
    try {
      const r = await axios.post(`${API_URL}/api/stores/${currentStore.id}/email-logs/${log.id}/resend`, {}, { withCredentials: true, timeout: 25000 });
      toast.success(r.data.message);
      fetchLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Échec du renvoi');
      fetchLogs();
    } finally { setResendingId(null); }
  };

  const fetchLogs = useCallback(() => {
    if (!currentStore) return;
    setLogsLoading(true);
    axios.get(`${API_URL}/api/stores/${currentStore.id}/email-logs?limit=50`, { withCredentials: true })
      .then(r => setEmailLogs(r.data || []))
      .catch(() => {})
      .finally(() => setLogsLoading(false));
  }, [currentStore]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  useEffect(() => {
    if (!currentStore) return;
    axios.get(`${API_URL}/api/stores/${currentStore.id}/settings/smtp`, { withCredentials: true })
      .then(r => {
        // Backend never returns the password. Track that a password is already stored server-side.
        const d = r.data || {};
        // Normalise encryption field for older records
        const enc = d.encryption || (d.use_tls === false ? 'ssl' : 'starttls');
        setSettings(prev => ({ ...prev, ...d, password: '', encryption: enc }));
        setHasSavedPassword(!!(d && d.enabled && d.host));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentStore]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/stores/${currentStore.id}/settings/smtp`, settings, { withCredentials: true });
      toast.success('Configuration SMTP sauvegardée');
      // If they sent a new password, mark that a password is stored server-side
      if (settings.password) setHasSavedPassword(true);
      // Clear the local password field to avoid resubmitting it accidentally
      setSettings(prev => ({ ...prev, password: '' }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally { setSaving(false); }
  };

  const handleTest = async () => {
    const email = (testEmail || '').trim();
    if (!email || !email.includes('@')) {
      toast.error('Renseignez une adresse email valide pour recevoir le test');
      return;
    }
    setTesting(true);
    try {
      const resp = await axios.post(
        `${API_URL}/api/stores/${currentStore.id}/settings/smtp/test`,
        { email },
        { withCredentials: true, timeout: 20000 }
      );
      toast.success(resp.data.message);
    } catch (e) {
      const detail = e.response?.data?.detail || (e.code === 'ECONNABORTED' ? 'Timeout: le serveur SMTP ne répond pas' : 'Erreur SMTP');
      toast.error(detail);
    } finally { setTesting(false); }
  };

  const update = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  if (!currentStore) return <div className="text-center py-12"><p className="text-slate-500">Sélectionnez une boutique</p></div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="smtp-settings-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Notifications email</h1>
        <p className="text-slate-500 mt-1">Configurez le serveur SMTP pour envoyer des emails de suivi à vos clients</p>
      </div>

      {loading ? (
        <div className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF] mx-auto" /></div>
      ) : (
        <>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><EnvelopeSimple size={18} /> Serveur SMTP</CardTitle>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Activer</Label>
                  <Switch checked={settings.enabled} onCheckedChange={v => update('enabled', v)} data-testid="smtp-enabled-switch" />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {!settings.enabled && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                  <Warning size={18} className="shrink-0 mt-0.5" />
                  <p>Les notifications email sont désactivées. Les changements de statut de commande ne déclencheront pas d'emails.</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Hôte SMTP</Label>
                  <Input value={settings.host} onChange={e => update('host', e.target.value)} placeholder="smtp.gmail.com" data-testid="smtp-host" />
                </div>
                <div>
                  <Label className="text-xs">Port</Label>
                  <Input type="number" value={settings.port} onChange={e => update('port', parseInt(e.target.value) || 587)} data-testid="smtp-port" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Identifiant</Label>
                  <Input value={settings.username} onChange={e => update('username', e.target.value)} placeholder="votre@email.com" data-testid="smtp-username" />
                </div>
                <div>
                  <Label className="text-xs">Mot de passe</Label>
                  <div className="relative">
                    <Input type={showPassword ? 'text' : 'password'} value={settings.password} onChange={e => update('password', e.target.value)} placeholder={hasSavedPassword ? '•••••••• (déjà enregistré)' : '••••••••'} className="pr-9" data-testid="smtp-password" />
                    <button type="button" onClick={() => setShowPassword(v => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      title={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                      data-testid="smtp-password-toggle">
                      {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {hasSavedPassword && !settings.password && (
                    <p className="text-[11px] text-slate-400 mt-1">Laissez vide pour conserver le mot de passe existant</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Email expéditeur</Label>
                  <Input value={settings.from_email} onChange={e => update('from_email', e.target.value)} placeholder="noreply@magere-viandes.fr" data-testid="smtp-from-email" />
                </div>
                <div>
                  <Label className="text-xs">Nom expéditeur</Label>
                  <Input value={settings.from_name} onChange={e => update('from_name', e.target.value)} placeholder="MAGERE Viandes" data-testid="smtp-from-name" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
                <div className="sm:col-span-2">
                  <Label className="text-xs">Chiffrement</Label>
                  <select
                    className="w-full h-9 rounded-md border border-slate-200 bg-white px-3 text-sm mt-1"
                    value={settings.encryption || 'starttls'}
                    onChange={e => {
                      const enc = e.target.value;
                      const defaultPort = enc === 'ssl' ? 465 : enc === 'starttls' ? 587 : 25;
                      setSettings(prev => ({
                        ...prev,
                        encryption: enc,
                        use_tls: enc !== 'ssl', // kept for backward compat
                        port: defaultPort,
                      }));
                    }}
                    data-testid="smtp-encryption-select"
                  >
                    <option value="starttls">STARTTLS (port 587) — Gmail, OVH, Outlook standard</option>
                    <option value="ssl">SSL / TLS implicite (port 465) — OVH SSL, Free, La Poste</option>
                    <option value="none">Aucun chiffrement (port 25) — non recommandé</option>
                  </select>
                  <p className="text-[11px] text-slate-500 mt-1">
                    {settings.encryption === 'ssl' && "Chiffrement dès la connexion. Le port 465 est requis."}
                    {settings.encryption === 'starttls' && "Connexion en clair puis upgrade chiffré. Le port 587 est requis."}
                    {settings.encryption === 'none' && "Aucun chiffrement : à utiliser uniquement en réseau interne de confiance."}
                  </p>
                </div>
                {((settings.encryption === 'ssl' && settings.port !== 465) ||
                  (settings.encryption === 'starttls' && settings.port !== 587) ||
                  (settings.encryption === 'none' && settings.port !== 25)) && (
                  <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-[11px]">
                    <Warning size={14} className="shrink-0 mt-0.5" />
                    <span>Attention : le port {settings.port} n&apos;est pas standard pour ce mode. Le test risque de timeout.</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex flex-col gap-3">
            <div className="flex gap-3">
              <Button onClick={handleSave} disabled={saving} className="bg-[#0052FF]" data-testid="smtp-save-btn">
                {saving ? <CircleNotch className="animate-spin mr-2" size={14} /> : <ShieldCheck className="mr-2" size={14} />}
                Sauvegarder
              </Button>
            </div>

            {settings.enabled && settings.host && (
              <Card className="border-dashed">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2"><PaperPlaneTilt size={16} /> Envoyer un email de test</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                    <div className="flex-1">
                      <Label className="text-xs">Adresse email destinataire</Label>
                      <Input
                        type="email"
                        value={testEmail}
                        onChange={e => setTestEmail(e.target.value)}
                        placeholder="destinataire@exemple.com"
                        data-testid="smtp-test-email"
                      />
                    </div>
                    <Button variant="outline" onClick={handleTest} disabled={testing || !testEmail} data-testid="smtp-test-btn">
                      {testing ? <CircleNotch className="animate-spin mr-2" size={14} /> : <PaperPlaneTilt className="mr-2" size={14} />}
                      Envoyer le test
                    </Button>
                  </div>
                  <p className="text-[11px] text-slate-500">Un email de test sera envoyé à cette adresse. Le timeout est fixé à 15 s.</p>
                </CardContent>
              </Card>
            )}
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <ClockCounterClockwise size={18} /> Historique des emails envoyés
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={fetchLogs} disabled={logsLoading} data-testid="email-logs-refresh">
                  <ArrowsClockwise size={14} className={logsLoading ? 'animate-spin' : ''} />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {emailLogs.length === 0 ? (
                <p className="text-sm text-slate-400 py-4 text-center" data-testid="email-logs-empty">Aucun email envoyé pour le moment</p>
              ) : (
                <div className="space-y-2" data-testid="email-logs-list">
                  {emailLogs.map(log => (
                    <div key={log.id} className="flex items-center gap-3 p-2.5 border border-slate-100 rounded-lg hover:bg-slate-50 transition-colors" data-testid={`email-log-${log.id}`}>
                      <div className={`w-2 h-2 rounded-full shrink-0 ${log.status === 'sent' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[13px] font-medium truncate">{log.subject}</span>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{EMAIL_TYPE_LABELS[log.type] || log.type}</Badge>
                        </div>
                        <p className="text-[11px] text-slate-400 truncate">
                          À : {log.to} — {new Date(log.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                          {log.status !== 'sent' && <span className="text-red-500 ml-1">Échec{log.error ? ` : ${log.error.slice(0, 60)}` : ''}</span>}
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => setViewedEmail(log)} data-testid={`email-log-view-${log.id}`}>
                        <Eye size={15} />
                      </Button>
                      {log.status !== 'sent' && (
                        <Button variant="outline" size="sm" className="text-xs h-7 px-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                          onClick={() => handleResend(log)} disabled={resendingId === log.id}
                          data-testid={`email-log-resend-${log.id}`}>
                          {resendingId === log.id ? <CircleNotch size={13} className="animate-spin" /> : <><PaperPlaneTilt size={13} className="mr-1" /> Renvoyer</>}
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Dialog open={!!viewedEmail} onOpenChange={() => setViewedEmail(null)}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle className="text-base pr-6">{viewedEmail?.subject}</DialogTitle>
              </DialogHeader>
              {viewedEmail && (
                <div className="flex-1 overflow-y-auto space-y-3">
                  <div className="text-xs text-slate-500 space-y-0.5">
                    <p><strong>À :</strong> {viewedEmail.to}</p>
                    <p><strong>Date :</strong> {new Date(viewedEmail.created_at).toLocaleString('fr-FR')}</p>
                    <p><strong>Statut :</strong> {viewedEmail.status === 'sent' ? '✅ Envoyé' : `❌ Échec — ${viewedEmail.error || ''}`}</p>
                  </div>
                  <iframe
                    title="email-content"
                    srcDoc={viewedEmail.html}
                    className="w-full h-[55vh] border border-slate-200 rounded-lg bg-white"
                    sandbox=""
                    data-testid="email-content-iframe"
                  />
                </div>
              )}
            </DialogContent>
          </Dialog>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Comment ça marche ?</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-slate-600 space-y-2">
              <p>Lorsque vous changez le statut d'une commande dans la gestion des commandes, un email est automatiquement envoyé au client avec :</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Le nouveau statut de la commande</li>
                <li>Le code de suivi unique</li>
                <li>Un récapitulatif de la commande</li>
              </ul>
              <p className="text-slate-400 text-xs mt-3">Exemples de serveurs SMTP : Gmail (smtp.gmail.com:587), OVH (ssl0.ovh.net:465), Outlook (smtp.office365.com:587)</p>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default SmtpSettingsPage;
